import { create } from "zustand";
import { Conversation } from "../api/models/Conversation";
import { Message } from "../api/models/Message";

type DeltaMap = Record<number, string>;

/** OpenAI starts a fresh response after a tool round-trip, so do not infer a
 * block boundary from its sequence numbers (which may also arrive out of order). */
interface StreamState {
  completedTextBlocks: string[];
  completedReasoningBlocks: string[];
  textDeltas: DeltaMap;
  reasoningDeltas: DeltaMap;
}

const emptyStream = (): StreamState => ({ completedTextBlocks: [], completedReasoningBlocks: [], textDeltas: {}, reasoningDeltas: {} });
const orderedText = (deltas: DeltaMap) => Object.keys(deltas).map(Number).sort((a, b) => a - b).map((sequence) => deltas[sequence]).join("");
const streamText = (blocks: string[], deltas: DeltaMap) => [...blocks, orderedText(deltas)].filter(Boolean).join("\n\n");
const cloneMessage = (message: Message): Message => ({ ...message, content: message.content?.map((content) => ({ ...content })) });

const withInFlightMessage = (conversation: Conversation, stream: StreamState): Conversation => {
  const messages = [...(conversation.messages || [])].map(cloneMessage);
  const text = streamText(stream.completedTextBlocks, stream.textDeltas);
  const thinkingSummary = streamText(stream.completedReasoningBlocks, stream.reasoningDeltas);
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.id === "in-flight-message") {
    lastMessage.content = [{ ...(lastMessage.content?.[0] || {}), text }];
    lastMessage.thinkingSummary = thinkingSummary || undefined;
  } else {
    messages.push({ id: "in-flight-message", role: Message.RoleEnum.Assistant, content: [{ text, fileCitations: [], images: [] }], thinkingSummary: thinkingSummary || undefined });
  }
  return { ...conversation, messages };
};

const pendingMessages = (conversation?: Conversation) => (conversation?.messages || []).filter((message) => message.id?.startsWith("temp-"));
const appendPending = (serverConversation: Conversation, current?: Conversation): Conversation => ({
  ...serverConversation,
  messages: [...(serverConversation.messages || []), ...pendingMessages(current).filter((message) =>
    !(serverConversation.messages || []).some((serverMessage) => serverMessage.role === message.role && serverMessage.content?.[0]?.text === message.content?.[0]?.text),
  )],
});

export interface ConversationState {
  currentConversation: Conversation | undefined;
  conversations: Conversation[];
  messageInProgress: boolean;
  streams: Record<string, StreamState>;
  setCurrentConversation: (conversation: Conversation | undefined) => void;
  setConversations: (conversations: Conversation[]) => void;
  setMessageInProgress: (inProgress: boolean) => void;
  addOptimisticMessage: (conversationId: string, message: Message) => void;
  confirmOptimisticMessage: (conversationId: string, temporaryId: string, messageId: string) => void;
  removeOptimisticMessage: (conversationId: string, temporaryId: string) => void;
  beginResponse: (conversationId: string) => void;
  applyMessageDelta: (conversationId: string, sequenceNumber: number, delta: string) => void;
  applyReasoningDelta: (conversationId: string, sequenceNumber: number, delta: string) => void;
  completeStream: (conversationId: string, serverConversation: Conversation) => void;
  failStream: (conversationId: string) => void;
  mergeServerConversation: (serverConversation: Conversation) => void;
  updateConversationName: (id: string, name: string) => void;
  updateConversationShareLink: (id: string, shareLink: string | undefined) => void;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  currentConversation: undefined,
  conversations: [],
  messageInProgress: false,
  streams: {},
  setCurrentConversation: (conversation) => set({ currentConversation: conversation }),
  setConversations: (conversations) => set({ conversations }),
  setMessageInProgress: (inProgress) => set({ messageInProgress: inProgress }),

  addOptimisticMessage: (conversationId, message) => {
    const current = get().currentConversation;
    if (!current || current.id !== conversationId) return;
    set({ currentConversation: { ...current, messages: [...(current.messages || []), message] }, messageInProgress: true });
  },
  confirmOptimisticMessage: (conversationId, temporaryId, messageId) => {
    const current = get().currentConversation;
    if (!current || current.id !== conversationId) return;
    set({ currentConversation: { ...current, messages: (current.messages || []).map((message) => message.id === temporaryId ? { ...message, id: messageId } : message) } });
  },
  removeOptimisticMessage: (conversationId, temporaryId) => {
    const current = get().currentConversation;
    if (!current || current.id !== conversationId) return;
    set({ currentConversation: { ...current, messages: (current.messages || []).filter((message) => message.id !== temporaryId) }, messageInProgress: Boolean(get().streams[conversationId]) });
  },

  beginResponse: (conversationId) => {
    const previous = get().streams[conversationId] || emptyStream();
    const text = orderedText(previous.textDeltas);
    const reasoning = orderedText(previous.reasoningDeltas);
    const next: StreamState = {
      completedTextBlocks: text ? [...previous.completedTextBlocks, text] : previous.completedTextBlocks,
      completedReasoningBlocks: reasoning ? [...previous.completedReasoningBlocks, reasoning] : previous.completedReasoningBlocks,
      textDeltas: {}, reasoningDeltas: {},
    };
    const streams = { ...get().streams, [conversationId]: next };
    const current = get().currentConversation;
    set({ streams, messageInProgress: true, currentConversation: current?.id === conversationId ? withInFlightMessage(current, next) : current });
  },
  applyMessageDelta: (conversationId, sequenceNumber, delta) => {
    const previous = get().streams[conversationId] || emptyStream();
    const next = { ...previous, textDeltas: { ...previous.textDeltas, [sequenceNumber]: delta } };
    const streams = { ...get().streams, [conversationId]: next };
    const current = get().currentConversation;
    set({ streams, messageInProgress: true, currentConversation: current?.id === conversationId ? withInFlightMessage(current, next) : current });
  },
  applyReasoningDelta: (conversationId, sequenceNumber, delta) => {
    const previous = get().streams[conversationId] || emptyStream();
    const next = { ...previous, reasoningDeltas: { ...previous.reasoningDeltas, [sequenceNumber]: delta } };
    const streams = { ...get().streams, [conversationId]: next };
    const current = get().currentConversation;
    set({ streams, messageInProgress: true, currentConversation: current?.id === conversationId ? withInFlightMessage(current, next) : current });
  },
  completeStream: (conversationId, serverConversation) => {
    const streams = { ...get().streams };
    delete streams[conversationId];
    const current = get().currentConversation;
    set({ streams, messageInProgress: Object.keys(streams).length > 0, currentConversation: current?.id === conversationId ? appendPending(serverConversation, current) : current });
  },
  failStream: (conversationId) => {
    const streams = { ...get().streams };
    delete streams[conversationId];
    const current = get().currentConversation;
    set({
      streams,
      messageInProgress: Object.keys(streams).length > 0,
      currentConversation: current?.id === conversationId
        ? { ...current, messages: (current.messages || []).filter((message) => message.id !== "in-flight-message") }
        : current,
    });
  },
  // A non-terminal refresh is allowed to update metadata, but never erase a local stream.
  mergeServerConversation: (serverConversation) => {
    const current = get().currentConversation;
    if (!current || current.id !== serverConversation.id) return;
    const stream = get().streams[serverConversation.id];
    const base = appendPending(serverConversation, current);
    set({ currentConversation: stream ? withInFlightMessage(base, stream) : base });
  },
  updateConversationName: (id, name) => set((state) => ({
    conversations: state.conversations.map((conversation) => conversation.id === id ? { ...conversation, name } : conversation),
    currentConversation: state.currentConversation?.id === id ? { ...state.currentConversation, name } : state.currentConversation,
  })),
  updateConversationShareLink: (id, shareLink) => set((state) => ({
    conversations: state.conversations.map((conversation) => conversation.id === id ? { ...conversation, shareLink } : conversation),
    currentConversation: state.currentConversation?.id === id ? { ...state.currentConversation, shareLink } : state.currentConversation,
  })),
}));
